import { useQueryClient } from '@tanstack/react-query';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Alert, Button, FormControlLabel, Stack, Switch, Typography } from '@mui/material';
import api from '../../../api/api';
import useAdminDraftGuard from '../useAdminDraftGuard';
import { useUserStore } from '../../../stores/userStore';

function VisibilitySession({ disabled, onBusyChange }) {
  const queryClient = useQueryClient();
  const [enabled, setEnabled] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const pending = useRef(false);
  const life = useRef({ live: true, read: 0 });
  useAdminDraftGuard(null, { busy: saving });
  useEffect(() => {
    const session = life.current;
    session.live = true;
    return () => {
      session.live = false;
      session.read += 1;
    };
  }, []);
  useEffect(() => {
    onBusyChange?.(saving);
  }, [saving, onBusyChange]);
  const load = useCallback(async () => {
    if (pending.current) return;
    const session = life.current;
    const request = ++session.read;
    const current = () => session.live && session.read === request;
    setBusy(true);
    setError('');
    try {
      const response = await api.getAuth('/admin/ads/events/home');
      if (!current()) return;
      if (typeof response.data?.data?.enabled !== 'boolean') throw new Error('Invalid settings');
      setEnabled(response.data.data.enabled);
      setLoaded(true);
    } catch {
      if (current()) setError('홈 이벤트 노출 설정을 불러오지 못했습니다.');
    } finally {
      if (current()) setBusy(false);
    }
  }, []);
  useEffect(() => {
    void load();
  }, [load]);
  const toggle = async (next) => {
    if (pending.current || disabled || !loaded || busy) return;
    pending.current = true;
    setSaving(true);
    setBusy(true);
    setError('');
    try {
      const response = await api.putAuth('/admin/ads/events/home', { enabled: next });
      if (!life.current.live) return;
      if (typeof response.data?.data?.enabled !== 'boolean') throw new Error('Invalid settings');
      setEnabled(response.data.data.enabled);
      void queryClient.invalidateQueries({ queryKey: ['public-events'] });
    } catch {
      if (life.current.live)
        setError('홈 이벤트 노출 설정을 저장하지 못했습니다. 다시 시도해주세요.');
    } finally {
      pending.current = false;
      if (life.current.live) {
        setBusy(false);
        setSaving(false);
      }
    }
  };
  return (
    <Stack spacing={1} sx={{ p: 2, border: 1, borderColor: 'divider', borderRadius: 1 }}>
      <FormControlLabel
        label="홈에 이벤트 섹션 표시"
        control={
          <Switch
            checked={enabled}
            disabled={disabled || !loaded || busy}
            onChange={(event) => void toggle(event.target.checked)}
          />
        }
      />
      <Typography variant="body2" color="text.secondary">
        공개 이벤트를 홈에서 좌우로 넘겨 볼 수 있게 표시합니다. 끄더라도 이벤트 및 공지사항의 목록과
        개별 이벤트 공개 설정은 유지됩니다. 공개 이벤트가 없으면 홈에는 표시되지 않습니다. 앱과
        웹에서 같은 공개 설정을 사용합니다.
      </Typography>
      {busy && <Typography variant="caption">설정 반영 중…</Typography>}
      {error && (
        <Alert
          severity="error"
          sx={{
            flexWrap: 'wrap',
            '& .MuiAlert-message': { minWidth: 0, flex: '1 1 100px', overflowWrap: 'anywhere' },
            '& .MuiAlert-action': { ml: 'auto', pl: 0 },
          }}
          action={
            !loaded ? (
              <Button onClick={load} disabled={disabled || busy}>
                재시도
              </Button>
            ) : undefined
          }
        >
          {error}
        </Alert>
      )}
    </Stack>
  );
}

export default function HomeEventVisibilityControl(props) {
  const user = useUserStore((state) => state.user);
  return <VisibilitySession key={JSON.stringify([user?.id, user?.role])} {...props} />;
}
